import axios from 'axios'
import {
  ADD_WAYPOINT,
  CLEAR_WAYPOINTS,
  RECEIVE_GEOCODE_RESULTS,
  REQUEST_GEOCODE_RESULTS,
  SET_WAYPOINT,
  UPDATE_TEXTINPUT,
  EMPTY_WAYPOINT,
  INSERT_WAYPOINT,
  RECEIVE_ROUTE_RESULTS,
  CLEAR_ROUTES,
  TOGGLE_PROVIDER_ISO,
  HIGHLIGHT_MNV,
  ZOOM_TO_MNV,
  UPDATE_INCLINE_DECLINE,
  RECEIVE_TRAFFIC_RESULTS,
  CLEAR_TRAFFIC_RESULTS,
  TOGGLE_TRAFFIC_MAP,
} from './types'

import {
  reverse_geocode,
  forward_geocode,
  parseGeocodeResponse,
} from 'utils/geocoder'

import {
  VALHALLA_OSM_URL,
  DRIVEPOINTS_BACKEND_URL,
  X_DP_MODE,
  buildDirectionsRequest,
  parseDirectionsGeometry,
} from 'utils/valhalla'
import { drivepointsRequestHeaders } from 'utils/drivepointsApi'
import {
  enrichTripWithTraffic,
  isTrafficProfile,
  tripTrafficCacheKey,
  resolveDepartAtIso,
} from 'traffic'
import { localDateTimeInputToUtcIso } from 'utils/dateTimeLocal'

import {
  sendMessage,
  showLoading,
  setOptimizing,
  filterProfileSettings,
  updatePermalink,
  zoomTo,
} from './commonActions'

const serverMapping = {
  [VALHALLA_OSM_URL]: 'OSM',
  [DRIVEPOINTS_BACKEND_URL]: 'Drivepoints',
}

export const makeRequest =
  ({ auth } = {}) =>
  (dispatch, getState) => {
    console.log('[makeRequest] auth', auth)
    if (!auth) {
      console.error('[makeRequest] auth is undefined')
      return
    }
    dispatch(updatePermalink())
    const { waypoints } = getState().directions
    const { profile, dateTime } = getState().common
    let { settings } = getState().common
    // if 2 results are selected
    const activeWaypoints = getActiveWaypoints(waypoints)
    if (activeWaypoints.length >= 2) {
      settings = filterProfileSettings(profile, settings)
      const valhallaRequest = buildDirectionsRequest({
        profile,
        activeWaypoints,
        settings,
        dateTime,
      })
      console.log('routeRequest', valhallaRequest)
      // if drivepoints auto or drivepoints truck profile call the drivepoints directions backend instead of valhalla
      if (profile === 'autodp' || profile === 'truckdp') {
        dispatch(fetchDrivepointsDirections(valhallaRequest, auth))
      } else {
        dispatch(fetchValhallaDirections(valhallaRequest, auth))
      }
    }
  }

const getActiveWaypoints = (waypoints) => {
  const activeWaypoints = []
  for (const waypoint of waypoints) {
    if (waypoint.geocodeResults.length > 0) {
      for (const result of waypoint.geocodeResults) {
        if (result.selected) {
          activeWaypoints.push(result)
          break
        }
      }
    }
  }
  return activeWaypoints
}

const fetchValhallaDirections = (valhallaRequest, auth) => (dispatch) => {
  dispatch(showLoading(true))

  let authHeaders = {}

  if (
    auth &&
    // only add auth headers if the url is a drivepoints url or localhost
    (VALHALLA_OSM_URL.includes('drivepoints.com') ||
      VALHALLA_OSM_URL.includes('localhost'))
  ) {
    authHeaders = {
      Authorization: `Bearer ${auth?.user?.access_token}`,
    }
    if (X_DP_MODE) {
      authHeaders['X-DP-mode'] = X_DP_MODE
    }
  }

  const config = {
    params: { json: JSON.stringify(valhallaRequest.json) },
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
  }
  axios
    .get(VALHALLA_OSM_URL + '/route', config)
    .then(({ data }) => {
      if (data.ok && data.data) {
        // Adjust to the new API response format
        data = data.data
      }
      data.decodedGeometry = parseDirectionsGeometry(data)

      if (data.alternates) {
        for (let i = 0; i < data.alternates.length; i++) {
          const alternate = data.alternates[i]
          data.alternates[i].decodedGeometry =
            parseDirectionsGeometry(alternate)
        }
      }
      dispatch(registerRouteResponse(VALHALLA_OSM_URL, data))
      dispatch(zoomTo(data.decodedGeometry))
      dispatch(maybeEnrichTraffic(VALHALLA_OSM_URL, data))
    })
    .catch(({ response }) => {
      if (!response || !response.data) {
        dispatch(
          sendMessage({
            type: 'error',
            icon: 'error',
            description: `${serverMapping[VALHALLA_OSM_URL]}: No response from server`,
            title: 'Network Error',
          })
        )
        return
      }

      let error_msg = response.data.error
      if (response.data.error_code === 154) {
        error_msg += ` for ${valhallaRequest.json.costing}.`
      }
      dispatch(clearRoutes(VALHALLA_OSM_URL))
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description: `${serverMapping[VALHALLA_OSM_URL]}: ${error_msg}`,
          title: `${response.data.status}`,
        })
      )
    })
    .finally(() => {
      setTimeout(() => {
        dispatch(showLoading(false))
      }, 500)
    })
}

const fetchDrivepointsDirections = (valhallaRequest, auth) => (dispatch) => {
  if (!auth) {
    console.error('[fetchDrivepointsDirections] auth is undefined')
    return
  }

  dispatch(showLoading(true))

  // FIXME: fall back to car costing until we implement autodp costing
  if (valhallaRequest.json.costing === 'autodp') {
    valhallaRequest.json.costing = 'auto'
    if (
      valhallaRequest.json.costing_options &&
      valhallaRequest.json.costing_options.autodp
    ) {
      valhallaRequest.json.costing_options.auto =
        valhallaRequest.json.costing_options.autodp
      delete valhallaRequest.json.costing_options.autodp
    }
  }

  // FIXME: fall back to truck costing until we implement truckdp costing
  if (valhallaRequest.json.costing === 'truckdp') {
    valhallaRequest.json.costing = 'truck'
    if (
      valhallaRequest.json.costing_options &&
      valhallaRequest.json.costing_options.truckdp
    ) {
      valhallaRequest.json.costing_options.truck =
        valhallaRequest.json.costing_options.truckdp
      delete valhallaRequest.json.costing_options.truckdp
    }
  }

  let authHeaders = {}

  if (
    auth &&
    // only add auth headers if the url is a drivepoints url or localhost
    (DRIVEPOINTS_BACKEND_URL.includes('drivepoints.com') ||
      DRIVEPOINTS_BACKEND_URL.includes('localhost'))
  ) {
    authHeaders = {
      Authorization: `Bearer ${auth?.user?.access_token}`,
    }
  }

  const config = {
    data: {
      // org_id: "<org_id>", // optional, if not provided, the first org in the user's orgs will be used
      params: valhallaRequest.json,
    },
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...drivepointsRequestHeaders({ requireIdempotency: true }),
    },
  }

  axios
    .post(DRIVEPOINTS_BACKEND_URL + '/route', config.data, {
      headers: config.headers,
    })
    .then(({ data }) => {
      if (data.ok && data.data) {
        // Adjust to the new API response format
        data = data.data
      }
      data.decodedGeometry = parseDirectionsGeometry(data)

      if (data.alternates) {
        for (let i = 0; i < data.alternates.length; i++) {
          const alternate = data.alternates[i]
          data.alternates[i].decodedGeometry =
            parseDirectionsGeometry(alternate)
        }
      }
      dispatch(registerRouteResponse(VALHALLA_OSM_URL, data))
      dispatch(zoomTo(data.decodedGeometry))
      dispatch(maybeEnrichTraffic(VALHALLA_OSM_URL, data))
    })
    .catch(({ response }) => {
      let error_msg = response.data.error
      if (response.data.error_code === 154) {
        error_msg += ` for ${valhallaRequest.json.costing}.`
      }
      dispatch(clearRoutes(VALHALLA_OSM_URL))
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description: `${serverMapping[VALHALLA_OSM_URL]}: ${error_msg}`,
          title: `${response.data.status}`,
        })
      )
    })
    .finally(() => {
      setTimeout(() => {
        dispatch(showLoading(false))
      }, 500)
    })
}

export const registerRouteResponse = (provider, data) => ({
  type: RECEIVE_ROUTE_RESULTS,
  payload: {
    provider,
    data,
  },
})

export const receiveTrafficResults = (provider, routeIndex, traffic) => ({
  type: RECEIVE_TRAFFIC_RESULTS,
  payload: { provider, routeIndex, traffic },
})

export const clearTrafficResults = (provider) => ({
  type: CLEAR_TRAFFIC_RESULTS,
  payload: { provider },
})

const trafficCache = new Map()

/**
 * After a successful Valhalla/Drivepoints route, optionally enrich with Mapbox traffic.
 * Does not block or clear the Valhalla route on failure.
 */
export const maybeEnrichTraffic =
  (provider, data) => async (dispatch, getState) => {
    const { trafficEnabled, profile, dateTime } = getState().common
    if (!trafficEnabled || !isTrafficProfile(profile)) {
      dispatch(clearTrafficResults(provider))
      return
    }

    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
    if (!accessToken) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          title: 'Traffic unavailable',
          description: 'REACT_APP_MAPBOX_ACCESS_TOKEN is not set',
        })
      )
      return
    }

    const trips = []
    if (data?.trip) {
      trips.push({ routeIndex: -1, trip: data.trip })
    }
    if (Array.isArray(data?.alternates)) {
      data.alternates.forEach((alt, i) => {
        if (alt?.trip) {
          trips.push({ routeIndex: i, trip: alt.trip })
        }
      })
    }

    for (const { routeIndex, trip } of trips) {
      // UI stores local datetime-local; Mapbox wants UTC depart_at (leave now / depart at only)
      let departAt = null
      if (dateTime?.type === 0) {
        departAt = resolveDepartAtIso({ dateTime })
      } else if (dateTime?.type === 1) {
        departAt = localDateTimeInputToUtcIso(dateTime.value)
      }
      const cacheKey = tripTrafficCacheKey(trip, { departAt, dateTime })
      const cached = trafficCache.get(cacheKey)
      if (cached) {
        dispatch(receiveTrafficResults(provider, routeIndex, cached))
        continue
      }

      dispatch(
        receiveTrafficResults(provider, routeIndex, {
          loading: true,
          ok: false,
          legs: [],
          totals: null,
        })
      )

      try {
        // eslint-disable-next-line no-await-in-loop
        const traffic = await enrichTripWithTraffic(trip, {
          accessToken,
          departAt,
          dateTime,
          cacheKey,
        })
        trafficCache.set(cacheKey, traffic)
        dispatch(receiveTrafficResults(provider, routeIndex, traffic))
      } catch (error) {
        dispatch(
          receiveTrafficResults(provider, routeIndex, {
            ok: false,
            loading: false,
            code: 'enrich_error',
            error: error?.message || 'Traffic enrichment failed',
            legs: [],
            totals: null,
          })
        )
      }
    }
  }

/**
 * Re-run traffic enrichment for current routes (e.g. after enabling the checkbox).
 */
export const refreshTrafficForCurrentRoutes = () => (dispatch, getState) => {
  const { results } = getState().directions
  const data = results[VALHALLA_OSM_URL]?.data
  if (data?.trip) {
    dispatch(maybeEnrichTraffic(VALHALLA_OSM_URL, data))
  } else {
    dispatch(clearTrafficResults(VALHALLA_OSM_URL))
  }
}

export const clearRoutes = (provider) => ({
  type: CLEAR_ROUTES,
  payload: provider,
})

const placeholderAddress = (index, lng, lat) => (dispatch) => {
  // placeholder until geocoder is complete
  // will add latLng to input field
  const addresses = [
    {
      title: '',
      displaylnglat: [lng, lat],
      key: index,
      addressindex: index,
    },
  ]
  dispatch(receiveGeocodeResults({ addresses, index: index }))
  dispatch(
    updateTextInput({
      inputValue: [lng.toFixed(6), lat.toFixed(6)].join(', '),
      index: index,
      addressindex: 0,
    })
  )
}

export const fetchReverseGeocodePerma = (object, auth) => (dispatch) => {
  dispatch(requestGeocodeResults({ index: object.index, reverse: true }))

  const { index } = object
  const { permaLast } = object
  const { lng, lat } = object.latLng

  if (index > 1) {
    dispatch(doAddWaypoint(true, permaLast))
  }

  reverse_geocode(lng, lat)
    .then((response) => {
      dispatch(
        processGeocodeResponse(
          response.data,
          index,
          true,
          [lng, lat],
          permaLast,
          auth
        )
      )
    })
    .catch((error) => {
      console.error(error) //eslint-disable-line
    })
  // .finally(() => {
  //   // always executed
  // })
}

export const fetchReverseGeocode = (object, auth) => (dispatch, getState) => {
  //dispatch(requestGeocodeResults({ index: object.index, reverse: true }))
  const { waypoints } = getState().directions

  let { index } = object
  const { fromDrag } = object
  const { lng, lat } = object.latLng

  if (index === -1) {
    index = waypoints.length - 1
  } else if (index === 1 && !fromDrag) {
    // insert waypoint from context menu
    dispatch(doAddWaypoint(true))

    index = waypoints.length - 2
  }

  dispatch(placeholderAddress(index, lng, lat))

  dispatch(requestGeocodeResults({ index, reverse: true }))

  reverse_geocode(lng, lat)
    .then((response) => {
      dispatch(
        processGeocodeResponse(
          response.data,
          index,
          true,
          [lng, lat],
          undefined,
          auth
        )
      )
    })
    .catch((error) => {
      console.error(error) //eslint-disable-line
    })
  // .finally(() => {
  //   // always executed
  // })
}

const getFallbackCenterLngLat = () => {
  try {
    if (localStorage.getItem('last_center')) {
      const lastCenter = JSON.parse(localStorage.getItem('last_center'))
      if (lastCenter && Array.isArray(lastCenter.center)) {
        const [lat, lng] = lastCenter.center
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return [lng, lat]
        }
      }
    }
  } catch (error) {
    console.warn('[getFallbackCenterLngLat] unable to read last_center', error)
  }

  if (process.env.REACT_APP_CENTER_COORDS) {
    const [lat, lng] =
      process.env.REACT_APP_CENTER_COORDS.split(',').map(Number)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return [lng, lat]
    }
  }

  return undefined
}

const getWaypointProximity = (waypoints, excludeIndex) => {
  for (const [index, waypoint] of waypoints.entries()) {
    if (index === excludeIndex) {
      continue
    }
    const selected =
      waypoint.geocodeResults &&
      waypoint.geocodeResults.find((result) => result.selected)
    const candidate = selected || waypoint.geocodeResults?.[0]
    if (candidate && Array.isArray(candidate.addresslnglat)) {
      return candidate.addresslnglat
    }
  }
  return undefined
}

const buildEmptyGeocodeResult = (lngLat) => {
  const safeLngLat = Array.isArray(lngLat) ? lngLat : []
  return {
    title: 'No addresses found',
    description: '',
    selected: false,
    addresslnglat: safeLngLat,
    sourcelnglat: safeLngLat,
    displaylnglat: safeLngLat,
    key: 0,
    addressindex: 0,
    isPlaceholder: true,
  }
}

export const fetchGeocode = (object) => (dispatch, getState) => {
  if (object.lngLat) {
    const addresses = [
      {
        title: object.lngLat.toString(),
        description: '',
        selected: false,
        addresslnglat: object.lngLat,
        sourcelnglat: object.lngLat,
        displaylnglat: object.lngLat,
        key: object.index,
        addressindex: 0,
      },
    ]
    dispatch(receiveGeocodeResults({ addresses, index: object.index }))
  } else {
    dispatch(requestGeocodeResults({ index: object.index }))

    const { directions } = getState()
    const proximity =
      getWaypointProximity(directions.waypoints, object.index) ||
      getFallbackCenterLngLat()
    forward_geocode(object.inputValue, {
      proximity,
      countries: 'us',
      countryCodes: 'us',
    })
      .then((response) => {
        dispatch(processGeocodeResponse(response.data, object.index))
      })
      .catch((error) => {
        console.error(error) //eslint-disable-line
      })
      .finally(() => {})
  }
}

const processGeocodeResponse =
  (data, index, reverse, lngLat, permaLast, auth) => (dispatch) => {
    let addresses = parseGeocodeResponse(data, lngLat)
    if (addresses.length === 0) {
      addresses = [buildEmptyGeocodeResult(lngLat)]
    }
    dispatch(receiveGeocodeResults({ addresses, index }))

    if (reverse) {
      dispatch(
        updateTextInput({
          inputValue: addresses[0].title,
          index: index,
          addressindex: 0,
        })
      )
      if (permaLast === undefined) {
        dispatch(makeRequest({ auth }))
        dispatch(updatePermalink())
      } else if (permaLast) {
        dispatch(makeRequest({ auth }))
        dispatch(updatePermalink())
      }
    }
  }

export const receiveGeocodeResults = (object) => ({
  type: RECEIVE_GEOCODE_RESULTS,
  payload: object,
})

export const requestGeocodeResults = (object) => ({
  type: REQUEST_GEOCODE_RESULTS,
  payload: object,
})

export const updateTextInput = (object) => ({
  type: UPDATE_TEXTINPUT,
  payload: object,
})

export const doRemoveWaypoint = (index, auth) => (dispatch, getState) => {
  if (index === undefined) {
    dispatch(clearWaypoints())
    Array(2)
      .fill()
      .map((_, i) => dispatch(doAddWaypoint()))
  } else {
    let waypoints = getState().directions.waypoints
    if (waypoints.length > 2) {
      dispatch(clearWaypoints(index))
      dispatch(makeRequest({ auth }))
    } else {
      dispatch(emptyWaypoint(index))
    }
    waypoints = getState().directions.waypoints
    if (getActiveWaypoints(waypoints).length < 2) {
      dispatch(clearRoutes(VALHALLA_OSM_URL))
    }
  }
  dispatch(updatePermalink())
}

export const isWaypoint = (index) => (dispatch, getState) => {
  const waypoints = getState().directions.waypoints
  if (waypoints[index].geocodeResults.length > 0) {
    dispatch(clearRoutes(VALHALLA_OSM_URL))
  }
}

export const highlightManeuver = (fromTo) => (dispatch, getState) => {
  const highlightSegment = getState().directions.highlightSegment
  // this is dehighlighting
  if (
    highlightSegment.startIndex === fromTo.startIndex &&
    highlightSegment.endIndex === fromTo.endIndex
  ) {
    fromTo.startIndex = -1
    fromTo.endIndex = -1
  }

  dispatch({
    type: HIGHLIGHT_MNV,
    payload: fromTo,
  })
}

export const zoomToManeuver = (zoomObj) => ({
  type: ZOOM_TO_MNV,
  payload: zoomObj,
})

export const clearWaypoints = (index) => ({
  type: CLEAR_WAYPOINTS,
  payload: { index: index },
})

export const emptyWaypoint = (index) => ({
  type: EMPTY_WAYPOINT,
  payload: { index: index },
})

export const updateInclineDeclineTotal = (object) => ({
  type: UPDATE_INCLINE_DECLINE,
  payload: object,
})

export const doAddWaypoint = (doInsert) => (dispatch, getState) => {
  const waypoints = getState().directions.waypoints
  let maxIndex = Math.max.apply(
    Math,
    waypoints.map((wp) => {
      return wp.id
    })
  )
  maxIndex = isFinite(maxIndex) === false ? 0 : maxIndex + 1

  const emptyWp = {
    id: maxIndex.toString(),
    geocodeResults: [],
    isFetching: false,
    userInput: '',
  }
  if (doInsert) {
    dispatch(insertWaypoint(emptyWp))
  } else {
    dispatch(addWaypoint(emptyWp))
  }
}

const insertWaypoint = (waypoint) => ({
  type: INSERT_WAYPOINT,
  payload: waypoint,
})

export const addWaypoint = (waypoint) => ({
  type: ADD_WAYPOINT,
  payload: waypoint,
})

export const setWaypoints = (waypoints) => ({
  type: SET_WAYPOINT,
  payload: waypoints,
})

export const showProvider = (provider, show, idx) => ({
  type: TOGGLE_PROVIDER_ISO,
  payload: {
    provider,
    show,
    idx,
  },
})

export const showTrafficOnMap = (provider, show, idx) => ({
  type: TOGGLE_TRAFFIC_MAP,
  payload: {
    provider,
    show,
    idx,
  },
})

export const optimizeRoute =
  ({ auth, returnToStart } = {}) =>
  (dispatch, getState) => {
    if (!auth) {
      console.error('[optimizeRoute] auth is undefined')
      dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: 'Authentication required to optimize route',
          title: 'Error',
        })
      )
      return
    }

    const { waypoints } = getState().directions
    const { profile, dateTime } = getState().common
    let { settings } = getState().common

    const activeWaypoints = getActiveWaypoints(waypoints)

    if (activeWaypoints.length < 3) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description: 'At least 3 locations are required to optimize',
          title: 'Insufficient Locations',
        })
      )
      return
    }

    if (activeWaypoints.length > 20) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description: 'Maximum 20 locations allowed for optimization',
          title: 'Too Many Locations',
        })
      )
      return
    }

    if (returnToStart && activeWaypoints.length > 19) {
      dispatch(
        sendMessage({
          type: 'warning',
          icon: 'warning',
          description:
            'Maximum 19 locations allowed when return to start is enabled',
          title: 'Too Many Locations',
        })
      )
      return
    }

    settings = filterProfileSettings(profile, settings)
    const valhallaRequest = buildDirectionsRequest({
      profile,
      activeWaypoints,
      settings,
      dateTime,
    })

    // Add return_to_start flag if enabled
    const optimizeRequest = {
      ...valhallaRequest.json,
      // return_to_start: returnToStart || false,
    }

    let authHeaders = {}

    if (
      auth &&
      (DRIVEPOINTS_BACKEND_URL.includes('drivepoints.com') ||
        DRIVEPOINTS_BACKEND_URL.includes('localhost'))
    ) {
      authHeaders = {
        Authorization: `Bearer ${auth?.user?.access_token}`,
      }
    }

    // Ensure every location in optimizeRequest is type: "break"
    if (Array.isArray(optimizeRequest.locations)) {
      optimizeRequest.locations = optimizeRequest.locations.map((loc) => ({
        ...loc,
        type: 'break',
      }))
    }

    // Remove exclude_polygons from optimizeRequest if it exists
    if (optimizeRequest.exclude_polygons !== undefined) {
      delete optimizeRequest.exclude_polygons
    }

    dispatch(setOptimizing(true))

    function handleOptimizedRouteResponse(dataObject) {
      try {
        const data = dataObject || {}
        // Check if response contains route data (trip object)
        if (data.trip) {
          // Response contains the optimized route, use it directly
          data.decodedGeometry = parseDirectionsGeometry(data)

          if (data.alternates) {
            for (let i = 0; i < data.alternates.length; i++) {
              const alternate = data.alternates[i]
              data.alternates[i].decodedGeometry =
                parseDirectionsGeometry(alternate)
            }
          }

          // Reorder waypoints based on optimized order if provided
          let shouldReorder = false
          let reorderedWaypoints = []

          if (data.optimized_order && Array.isArray(data.optimized_order)) {
            const optimizedOrder = data.optimized_order

            for (const orderItem of optimizedOrder) {
              let waypointIndex
              if (typeof orderItem === 'number') {
                waypointIndex = orderItem
              } else if (orderItem.index !== undefined) {
                waypointIndex = orderItem.index
              } else {
                // Try to match by location coordinates
                const lat = orderItem.lat || orderItem.location?.lat
                const lon = orderItem.lon || orderItem.location?.lon
                waypointIndex = waypoints.findIndex((wp) => {
                  const selectedResult = wp.geocodeResults.find(
                    (result) => result.selected
                  )
                  if (selectedResult) {
                    const wpLat = selectedResult.displaylnglat[1]
                    const wpLon = selectedResult.displaylnglat[0]
                    return (
                      Math.abs(wpLat - lat) < 0.0001 &&
                      Math.abs(wpLon - lon) < 0.0001
                    )
                  }
                  return false
                })
              }

              if (waypointIndex >= 0 && waypointIndex < waypoints.length) {
                reorderedWaypoints.push(waypoints[waypointIndex])
              }
            }

            // If return to start is enabled, add the first waypoint at the end
            if (returnToStart && reorderedWaypoints.length > 0) {
              reorderedWaypoints.push(reorderedWaypoints[0])
            }

            // Only reorder if we got valid results
            if (reorderedWaypoints.length === activeWaypoints.length) {
              shouldReorder = true
            } else {
              // Fallback: try simple index-based reordering
              const simpleReorder = optimizedOrder
                .filter(
                  (idx) => typeof idx === 'number' && idx < waypoints.length
                )
                .map((idx) => waypoints[idx])

              if (simpleReorder.length === activeWaypoints.length) {
                if (returnToStart && simpleReorder.length > 0) {
                  simpleReorder.push(simpleReorder[0])
                }
                reorderedWaypoints = simpleReorder
                shouldReorder = true
              }
            }
          } else if (
            data.trip &&
            data.trip.locations &&
            Array.isArray(data.trip.locations)
          ) {
            // If no optimized_order but trip.locations has original_index, reorder based on trip.locations
            const hasOriginalIndex = data.trip.locations.some(
              (loc) => loc.original_index !== undefined
            )
            if (hasOriginalIndex) {
              // Create a map of original_index to waypoint
              const waypointMap = {}
              waypoints.forEach((wp, idx) => {
                waypointMap[idx] = wp
              })

              // Reorder based on trip.locations order (skip first location if it's the start)
              const locationsToReorder = data.trip.locations.filter(
                (loc) => loc.original_index !== undefined
              )

              reorderedWaypoints = locationsToReorder
                .map((loc) => {
                  return waypointMap[loc.original_index]
                })
                .filter(Boolean)

              // If return to start is enabled, add the first waypoint at the end
              if (returnToStart && reorderedWaypoints.length > 0) {
                reorderedWaypoints.push(reorderedWaypoints[0])
              }

              if (reorderedWaypoints.length === activeWaypoints.length) {
                shouldReorder = true
              }
            }
          }

          if (shouldReorder) {
            dispatch(setWaypoints(reorderedWaypoints))
          }

          // Register the route response without triggering a new computation
          // Ensure main trip is used as default (alternates are already handled)
          dispatch(registerRouteResponse(VALHALLA_OSM_URL, data))
          dispatch(zoomTo(data.decodedGeometry))
          dispatch(setOptimizing(false))
        } else if (
          data.optimized_order &&
          Array.isArray(data.optimized_order)
        ) {
          // Response only contains optimized order, reorder waypoints and compute route
          const optimizedOrder = data.optimized_order
          const reorderedWaypoints = []

          for (const orderItem of optimizedOrder) {
            let waypointIndex
            if (typeof orderItem === 'number') {
              waypointIndex = orderItem
            } else if (orderItem.index !== undefined) {
              waypointIndex = orderItem.index
            } else {
              const lat = orderItem.lat || orderItem.location?.lat
              const lon = orderItem.lon || orderItem.location?.lon
              waypointIndex = waypoints.findIndex((wp) => {
                const selectedResult = wp.geocodeResults.find(
                  (result) => result.selected
                )
                if (selectedResult) {
                  const wpLat = selectedResult.displaylnglat[1]
                  const wpLon = selectedResult.displaylnglat[0]
                  return (
                    Math.abs(wpLat - lat) < 0.0001 &&
                    Math.abs(wpLon - lon) < 0.0001
                  )
                }
                return false
              })
            }

            if (waypointIndex >= 0 && waypointIndex < waypoints.length) {
              reorderedWaypoints.push(waypoints[waypointIndex])
            }
          }

          if (returnToStart && reorderedWaypoints.length > 0) {
            reorderedWaypoints.push(reorderedWaypoints[0])
          }

          if (reorderedWaypoints.length === activeWaypoints.length) {
            dispatch(setWaypoints(reorderedWaypoints))
            // Compute route with reordered waypoints
            dispatch(makeRequest({ auth }))
            dispatch(setOptimizing(false))
          } else {
            throw new Error('Could not match optimized order to waypoints')
          }
        } else {
          throw new Error('Invalid response format from optimize endpoint')
        }
      } catch (error) {
        // Ensure spinner stops even if there's an error in response handling
        dispatch(setOptimizing(false))
        throw error
      }
    }

    function handleOptimizedRouteError(error) {
      console.error('[optimizeRoute] error', error)
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to optimize route'
      dispatch(
        sendMessage({
          type: 'error',
          icon: 'error',
          description: errorMessage,
          title: 'Optimization Error',
        })
      )
      dispatch(setOptimizing(false))
    }

    function handleOptimizedRouteFinally() {
      dispatch(setOptimizing(false))
    }

    const config = {
      params: { json: JSON.stringify(optimizeRequest) },
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...drivepointsRequestHeaders({ requireIdempotency: true }),
      },
    }

    if (
      optimizeRequest.costing === 'autodp' ||
      optimizeRequest.costing === 'truckdp'
    ) {
      // when we are using drivepoints backend we must send auto or truck for costing.
      if (optimizeRequest.costing === 'autodp') {
        optimizeRequest.costing = 'auto'
        optimizeRequest.costing_options.auto =
          optimizeRequest.costing_options.autodp
        delete optimizeRequest.costing_options.autodp
      } else if (optimizeRequest.costing === 'truckdp') {
        optimizeRequest.costing = 'truck'
        optimizeRequest.costing_options.truck =
          optimizeRequest.costing_options.truckdp
        delete optimizeRequest.costing_options.truckdp
      }

      console.log('optimizeRequest', optimizeRequest)

      axios
        // .get(VALHALLA_OSM_URL + '/optimized_route', config)
        .post(
          DRIVEPOINTS_BACKEND_URL + '/optimized_route',
          { params: optimizeRequest },
          {
            headers: config.headers,
          }
        )
        .then(({ data }) => {
          if (data.ok && data.data) {
            // Adjust to the new API response format
            data = data.data
          }
          handleOptimizedRouteResponse(data)
        })
        .catch(handleOptimizedRouteError)
        .finally(handleOptimizedRouteFinally)
    } else {
      // use valhalla backend
      axios
        .get(VALHALLA_OSM_URL + '/optimized_route', config)
        .then(({ data }) => {
          if (data.ok && data.data) {
            // Adjust to the new API response format
            data = data.data
          }
          handleOptimizedRouteResponse(data)
        })
        .catch(handleOptimizedRouteError)
        .finally(handleOptimizedRouteFinally)
    }
  }
